import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { lazy, Suspense } from "react";

// Eager load homepage for better UX
import Index from "./pages/Index";

// Lazy load all other pages
const EarlyAccess = lazy(() => import("./pages/EarlyAccess"));
const Guides = lazy(() => import("./pages/Guides"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const AcceptableUse = lazy(() => import("./pages/AcceptableUse"));
const DataProcessingAddendum = lazy(() => import("./pages/DataProcessingAddendum"));
const CustomerProofAutomation = lazy(() => import("./pages/CustomerProofAutomation"));
const LLMSnippets = lazy(() => import("./pages/LLMSnippets"));
const GTMWithProof = lazy(() => import("./pages/guides/GTMWithProof"));
const CustomerProofFlywheel = lazy(() => import("./pages/guides/CustomerProofFlywheel"));
const CustomerStoryCollection = lazy(() => import("./pages/guides/CustomerStoryCollection"));
const TestimonialAutomation = lazy(() => import("./pages/guides/TestimonialAutomation"));
const CaseStudyPlaybook = lazy(() => import("./pages/guides/CaseStudyPlaybook"));
const GenerateSalesCollateralAI = lazy(() => import("./pages/guides/GenerateSalesCollateralAI"));
const VOCAIMethods = lazy(() => import("./pages/guides/VOCAIMethods"));
const AICaseStudyTools = lazy(() => import("./pages/guides/AICaseStudyTools"));
const TellCaseyVsAgencies = lazy(() => import("./pages/guides/TellCaseyVsAgencies"));
const TellCaseyVsOthers = lazy(() => import("./pages/guides/TellCaseyVsOthers"));
const TrustBeforeFeatures = lazy(() => import("./pages/guides/TrustBeforeFeatures"));
const FutureOfCustomerMarketing = lazy(() => import("./pages/guides/FutureOfCustomerMarketing"));
const CustomerInterviewScript = lazy(() => import("./pages/guides/CustomerInterviewScript"));
const CaseStudyTemplate = lazy(() => import("./pages/guides/CaseStudyTemplate"));
const TestimonialTemplateLibrary = lazy(() => import("./pages/guides/TestimonialTemplateLibrary"));
const CustomerProofSOP = lazy(() => import("./pages/guides/CustomerProofSOP"));
const Glossary = lazy(() => import("./pages/Glossary"));
const Templates = lazy(() => import("./pages/Templates"));
const Playbooks = lazy(() => import("./pages/Playbooks"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const WhatIsCustomerProofAutomation = lazy(() => import("./pages/WhatIsCustomerProofAutomation"));
const Resources = lazy(() => import("./pages/Resources"));
const MarketResearchInsights = lazy(() => import("./pages/MarketResearchInsights"));
const HospitalityAI = lazy(() => import("./pages/HospitalityAI"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/early-access" element={<EarlyAccess />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/guides/customer-proof-automation" element={<CustomerProofAutomation />} />
              <Route path="/llm-snippets" element={<LLMSnippets />} />
              <Route path="/guides/gtm-with-proof" element={<GTMWithProof />} />
              <Route path="/guides/customer-proof-flywheel" element={<CustomerProofFlywheel />} />
              <Route path="/guides/customer-story-collection" element={<CustomerStoryCollection />} />
              <Route path="/guides/testimonial-automation" element={<TestimonialAutomation />} />
              <Route path="/guides/case-study-playbook" element={<CaseStudyPlaybook />} />
              <Route path="/guides/generate-sales-collateral-ai" element={<GenerateSalesCollateralAI />} />
              <Route path="/guides/voc-ai-methods" element={<VOCAIMethods />} />
              <Route path="/guides/ai-case-study-tools" element={<AICaseStudyTools />} />
              <Route path="/guides/tellcasey-vs-agencies" element={<TellCaseyVsAgencies />} />
              <Route path="/guides/tellcasey-vs-others" element={<TellCaseyVsOthers />} />
              <Route path="/guides/trust-before-features" element={<TrustBeforeFeatures />} />
              <Route path="/guides/future-of-customer-marketing" element={<FutureOfCustomerMarketing />} />
              <Route path="/guides/customer-interview-script" element={<CustomerInterviewScript />} />
              <Route path="/guides/case-study-template" element={<CaseStudyTemplate />} />
              <Route path="/guides/testimonial-template-library" element={<TestimonialTemplateLibrary />} />
              <Route path="/guides/customer-proof-sop" element={<CustomerProofSOP />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/templates" element={<Templates />} />
              <Route path="/playbooks" element={<Playbooks />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/what-is-customer-proof-automation" element={<WhatIsCustomerProofAutomation />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/solutions/market-research-ai" element={<MarketResearchInsights />} />
              <Route path="/mr" element={<Navigate to="/solutions/market-research-ai" replace />} />
              <Route path="/solutions/hospitality-ai" element={<HospitalityAI />} />
              <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms-of-service" element={<TermsOfService />} />
              <Route path="/legal/acceptable-use" element={<AcceptableUse />} />
              <Route path="/legal/dpa" element={<DataProcessingAddendum />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
